import { IdpAuthError } from './provider';

import { sessionStorageSeguro } from '@/lib/storage';

/**
 * O estado que precisa atravessar o redirect para o IDP: `state`,
 * `code_verifier`, `nonce` e para onde voltar.
 *
 * Vai em `sessionStorage`, e nao em memoria, porque o navegador sai do app e
 * volta — memoria nao sobrevive a isso. E `sessionStorage`, nao
 * `localStorage`: e por aba e morre com ela.
 *
 * Isto **nao** e token. O contrato manda guardar `state -> code_verifier` no
 * servidor ou em cookie httpOnly assinado (`fluxo-oidc.md`, passo 1); um client
 * publico de navegador nao tem nem um nem outro, e o `code_verifier` e inutil
 * sozinho — quem o rouba precisaria tambem interceptar o `code`, que e o que o
 * PKCE existe para impedir.
 */

// Nomes duplicados de proposito no `index.html`: a limpeza por versao de la
// apaga o sessionStorage depois do boot e preserva estas duas chaves pelo nome.
const PENDENTE_KEY = 'sapron-idp-login-pendente';
const SESSAO_KEY = 'sapron-idp-sessao';

export interface PendingLogin {
  readonly state: string;
  readonly codeVerifier: string;
  readonly nonce: string;
  /** Path + query + hash para onde voltar depois do callback. */
  readonly returnTo: string;
  /** Tentativa `prompt=none` de restaurar a sessao depois de um reload. */
  readonly silent: boolean;
}

export function savePendingLogin(pending: PendingLogin): void {
  sessionStorageSeguro()?.setItem(PENDENTE_KEY, JSON.stringify(pending));
}

export function clearPendingLogin(): void {
  sessionStorageSeguro()?.removeItem(PENDENTE_KEY);
}

/**
 * Recupera e **consome** a tentativa de login que corresponde ao `state` que
 * voltou. One-shot: repetir o callback com o mesmo `state` (F5 na URL do
 * callback, link colado) nao troca o code de novo.
 */
export function takePendingLogin(state: string | null): PendingLogin {
  const bruto = sessionStorageSeguro()?.getItem(PENDENTE_KEY);
  clearPendingLogin();

  if (bruto === null || bruto === undefined) {
    throw new IdpAuthError(
      'state_sem_tentativa',
      'Callback do IDP sem tentativa de login em aberto.',
    );
  }

  const pending = JSON.parse(bruto) as PendingLogin;
  if (state === null || state !== pending.state) {
    throw new IdpAuthError('state_divergente', 'O `state` da volta nao e o que este app emitiu.');
  }
  return pending;
}

/**
 * A marca de "esta aba tinha sessao do IDP".
 *
 * Nao e token nem sessao: e um `"1"` que autoriza o app, no proximo boot, a
 * tentar `prompt=none` contra a sessao SSO. Sem ela o app redirecionaria para
 * o IDP toda vez que alguem abrisse a tela de login por senha.
 */
export function markIdpSession(): void {
  sessionStorageSeguro()?.setItem(SESSAO_KEY, '1');
}

export function clearIdpSession(): void {
  sessionStorageSeguro()?.removeItem(SESSAO_KEY);
}

/**
 * Le a marca e **apaga**, antes de qualquer redirect.
 *
 * Apagar antes e o que impede o laco: uma tentativa silenciosa que falhe nao
 * encontra marca no boot seguinte, entao o pior caso e um redirect perdido,
 * nunca um ciclo.
 */
export function consumeIdpSession(): boolean {
  const marca = sessionStorageSeguro()?.getItem(SESSAO_KEY);
  clearIdpSession();
  return marca === '1';
}
